import { type FormEvent, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Overline } from '@/components/atoms/Eyebrow';
import FormField from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { ChoiceCards, useServerErrorMessage } from '@/components/organisms/FishGroupForms';
import type { TimelineFishGroup } from '@/domain/fishGroups';
import {
    isStockingCycleNameTaken,
    lineageStockingDefaults,
    type StockingAssignmentTarget,
    useGetStockingLineage,
    useStockingAssignment,
} from '@/domain/stockingCycles';
import { now } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import {
    assignOffer,
    concurrentStockings,
    defaultAssignChoice,
    NEW_STOCKING_CHOICE,
} from '../../helpers/stockingAssignment';
import type { BoardCycle } from '../../types';
import ModalFormActions from '../ModalFormActions/ModalFormActions';
import ActionDialogTitle from '../SiteActionDialog/ActionDialogTitle';

import LineageGroups from './LineageGroups';
import LineageRefusal from './LineageRefusal';

import styles from './StockingAssignment.module.scss';

interface AssignStockingModalProps {
    clientId: number;
    locationId: number;
    /** Real names: the dialog never mounts in demo mode. */
    group: TimelineFishGroup;
    /** Every stocking of the site, as the selector lists them. */
    cycles: BoardCycle[];
    locationName: string;
    onClose: () => void;
    /** The stocking the lineage went into: the one picked, or the one created for it. */
    onDone: (stockingCycleId: number) => void;
}

/**
 * "Add to stocking": the group's whole lineage goes into a stocking of the site, picked from
 * the list or created here. Refused before anything is sent when the lineage leaves the site
 * or the client, or is spread over several stockings.
 */
export default function AssignStockingModal(props: AssignStockingModalProps) {
    const { clientId, locationId, group, cycles, locationName, onClose, onDone } = props;
    const { t, i18n } = useTranslation('fishGroups');
    const nameId = useId();
    const listFormat = new Intl.ListFormat(parseLocale(i18n.language), { type: 'conjunction' });
    const lineage = useGetStockingLineage(clientId, locationId, group.id);
    const actions = useStockingAssignment(clientId, locationId);
    const serverError = useServerErrorMessage(actions.error);
    // A stocking created here for an assignment that then failed is a choice like any other,
    // whether or not the refetched list has it yet.
    const created = actions.createdCycle;
    const createdMissing = created !== null && !cycles.some((cycle) => cycle.id === created.id);
    const names = useMemo(
        () => [...cycles.map((cycle) => cycle.name), ...(createdMissing ? [created.name] : [])],
        [cycles, createdMissing, created]
    );
    const nameOf = (id: number) =>
        cycles.find((cycle) => cycle.id === id)?.name ??
        (created?.id === id ? created.name : String(id));

    const offer = lineage.data ? assignOffer(lineage.data, cycles) : null;
    const [picked, setPicked] = useState<string | null>(null);
    const choice = picked ?? (offer ? defaultAssignChoice(offer) : NEW_STOCKING_CHOICE);
    const defaults = useMemo(
        () => (lineage.data ? lineageStockingDefaults(lineage.data, names, now()) : null),
        [lineage.data, names]
    );
    const [typedName, setTypedName] = useState<string | null>(null);
    const newName = typedName ?? defaults?.name ?? '';

    // After a failed assignment, the retry starts on the stocking already created for it.
    useEffect(() => {
        if (created) setPicked(String(created.id));
    }, [created]);

    const creating = choice === NEW_STOCKING_CHOICE;
    const nameError = !creating
        ? null
        : newName.trim() === ''
          ? t('actions.errors.required')
          : isStockingCycleNameTaken(newName, names)
            ? t('actions.errors.codes.stockingCycleNameTaken')
            : null;
    // A completed stocking takes the lineage too, but a live group in it reopens the stocking.
    const pickedCompleted = cycles.find(
        (cycle) => String(cycle.id) === choice && cycle.status === 'completed'
    );
    const liveGroup =
        lineage.data?.groups.find((g) => g.isLive && g.requested) ??
        lineage.data?.groups.find((g) => g.isLive);
    const canSubmit =
        offer !== null && (offer.kind === 'choose' || offer.kind === 'joins') && !nameError;
    const targetName = creating ? newName.trim() : nameOf(Number(choice));
    // The site type's one-ongoing-stocking rule, foreseen from the preview: the same warning
    // the backend sends with the assignment.
    const predictedConcurrent =
        canSubmit && lineage.data
            ? concurrentStockings(lineage.data, cycles, creating ? null : Number(choice))
            : [];
    const ongoingText = (
        key: 'willHave' | 'nowHas',
        stockingName: string,
        others: readonly string[]
    ) => {
        const names = [stockingName, ...others];
        const site = locationName;
        const list = listFormat.format(names);
        return key === 'willHave'
            ? t('stockingAssignment.assign.concurrent.willHave', {
                  site,
                  count: names.length,
                  names: list,
              })
            : t('stockingAssignment.assign.concurrent.nowHas', {
                  site,
                  count: names.length,
                  names: list,
              });
    };

    const close = () => {
        if (actions.isLoading) return;
        actions.reset();
        onClose();
    };

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (!canSubmit || !defaults) return;
        const target: StockingAssignmentTarget = creating
            ? { kind: 'new', stocking: { ...defaults, name: newName.trim() } }
            : { kind: 'existing', stockingCycleId: Number(choice) };
        const result = await actions.assign(group.id, target).catch(() => null);
        if (!result) return;
        // The stocking the groups went into, whatever the form shows by now.
        const savedName = result.createdName ?? nameOf(result.stockingCycleId);
        ToastInfo({
            content: t('stockingAssignment.assign.successToast', {
                count: result.movedCount,
                name: savedName,
            }),
            status: 'success',
            icon: 'success',
            options: { toastId: 'stocking-assignment-success', autoClose: 5000 },
        });
        if (result.concurrentStockingIds.length > 0) {
            ToastInfo({
                content: ongoingText('nowHas', savedName, result.concurrentStockingIds.map(nameOf)),
                status: 'warning',
                type: 'warning',
                icon: 'optoscale',
                options: { toastId: 'stocking-assignment-concurrent', autoClose: 8000 },
            });
        }
        actions.reset();
        onDone(result.stockingCycleId);
    };

    const choiceOptions =
        offer?.kind === 'choose'
            ? [
                  ...offer.cycles.map((cycle) => ({
                      value: String(cycle.id),
                      label: cycle.name,
                      description:
                          cycle.status === 'completed'
                              ? t('stockingAssignment.assign.completedDescription')
                              : t(`board.cycleStatus.${cycle.status}`),
                  })),
                  ...(createdMissing
                      ? [
                            {
                                value: String(created.id),
                                label: created.name,
                                description: t('board.cycleStatus.planned'),
                            },
                        ]
                      : []),
                  {
                      value: NEW_STOCKING_CHOICE,
                      label: t('stockingAssignment.assign.newStocking'),
                      description: t('stockingAssignment.assign.newStockingDescription', {
                          site: locationName,
                      }),
                  },
              ]
            : [];

    const body = (
        <form className={styles.form} onSubmit={submit} noValidate>
            <LineageGroups
                lineage={lineage}
                cycleName={(id) => cycles.find((cycle) => cycle.id === id)?.name ?? ''}
            />
            <LineageRefusal offer={offer} />
            {offer?.kind === 'joins' && (
                <Alert tone="info" size="small">
                    {t('stockingAssignment.assign.joinsExisting', { name: offer.cycle.name })}
                </Alert>
            )}
            {offer?.kind === 'choose' && (
                <section className={styles.section}>
                    <Overline as="h3" className={styles.sectionLabel}>
                        {t('stockingAssignment.assign.stockingHeading')}
                    </Overline>
                    <ChoiceCards
                        options={choiceOptions}
                        value={choice}
                        onChange={setPicked}
                        aria-label={t('stockingAssignment.assign.stockingHeading')}
                    />
                    {creating && (
                        <FormField
                            label={t('stockingAssignment.assign.newName')}
                            htmlFor={nameId}
                            error={nameError}
                            className={styles.nameField}>
                            <TextField
                                id={nameId}
                                value={newName}
                                maxLength={255}
                                onChange={(event) => setTypedName(event.target.value)}
                            />
                        </FormField>
                    )}
                </section>
            )}
            {predictedConcurrent.length > 0 && (
                <Alert tone="warning" size="small">
                    {ongoingText(
                        'willHave',
                        targetName,
                        predictedConcurrent.map((cycle) => cycle.name)
                    )}
                </Alert>
            )}
            {canSubmit && pickedCompleted && liveGroup && (
                <Alert tone="warning" size="small">
                    {t('stockingAssignment.assign.reopensCompleted', {
                        name: pickedCompleted.name,
                        group: liveGroup.name,
                    })}
                </Alert>
            )}
            {serverError && <p className={styles.serverError}>{serverError}</p>}
            <ModalFormActions
                cancelLabel={t('actions.cancel')}
                submitLabel={t('stockingAssignment.assign.submit')}
                isSubmitting={actions.isLoading}
                submitDisabled={!canSubmit}
                onCancel={close}
            />
        </form>
    );

    return (
        <ModalComponent
            isOpen
            size="lg"
            onClose={close}
            title={
                <ActionDialogTitle
                    kind="fishGroup"
                    title={t('stockingAssignment.assign.title')}
                    groupName={group.name}
                    penName={null}
                    bioscopeIds={[]}
                />
            }
            body={body}
            backdrop="static"
        />
    );
}
